/**
 * Locale-aware numeric and date formatting for buyer-facing surfaces.
 *
 * Why a separate helper from `displayCurrency.formatCurrencyAmount`:
 *   `formatCurrencyAmount` is currency-suffixed; this one returns the bare
 *   number. Both now use the SAME digits.
 *
 * ⚠️ CORRECTED 2026-09-21 — this block used to say the opposite, and was the
 * bug. It read: "Phase 5D.5 calls for Eastern Arabic numerals in Arabic mode
 * (٣٫٧٥), so this module uses `ar-SA` without the `-u-nu-latn` override."
 * That is where the mixed numerals came from: two helpers rendering numbers
 * on ONE card, one in ٣٠٠٫٠٠ and the other in 300.00, because
 * `formatCurrencyAmount` had always forced `-u-nu-latn` and this had not.
 *
 * THE RULE, owner 2026-09-21: **all numbers are Latin digits in all three
 * languages.** It is what the dashboard already did, what the footer's CR and
 * VAT lines do, and what a trader comparing two quotes needs — a price is read
 * against other prices, invoices and bank transfers, none of which are in
 * Eastern Arabic numerals.
 *
 * `ar-SA-u-nu-latn` keeps the Arabic locale's grouping and decimal
 * punctuation and swaps only the digits, so the number still reads as Arabic
 * typography without becoming unreadable against an invoice.
 *
 * Naming note: the spec calls this `formatPriceLocale`. The helper is
 * actually general-purpose numeric formatting (CBM, weights, quantities,
 * prices) — the `fractionDigits` option lets the caller pick precision.
 */

const NULL_PLACEHOLDER = '—';

function pickNumberLocale(lang) {
  // ar-SA-u-nu-latn → Arabic grouping/decimal punctuation, LATIN digits
  // zh-CN          → Latin digits with Chinese grouping
  // en-US          → Latin digits
  // Identical to displayCurrency.js:168, deliberately: the two helpers render
  // numbers side by side on the same card.
  return lang === 'ar' ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US';
}

// Format a numeric value for buyer-side display. Returns '—' for any
// null/undefined/NaN/non-finite input (never "NaN" or "undefined").
export function formatPriceLocale(value, lang = 'en', { fractionDigits = 2 } = {}) {
  if (value === null || value === undefined || value === '') return NULL_PLACEHOLDER;
  const n = Number(value);
  if (!Number.isFinite(n)) return NULL_PLACEHOLDER;
  return n.toLocaleString(pickNumberLocale(lang), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

// Format an ISO/Date-parseable string in the buyer's locale. Returns null
// for null/empty input (so callers can use && short-circuit), and falls
// back to the raw value when the date is unparseable rather than
// rendering "Invalid Date".
export function formatDateLocale(iso, lang = 'en') {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(pickNumberLocale(lang), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch (_e) {
    return iso;
  }
}
